import {createApi} from '@reduxjs/toolkit/query/react'
import {baseQuery} from '../base-query'

export enum CallStatus {
  Ringing = 'ringing',
  InProgress = 'in_progress',
  Completed = 'completed',
  Missed = 'missed',
  Declined = 'declined',
  Failed = 'failed',
  Cancelled = 'cancelled',
  TimedOut = 'timed_out',
}

/**
 * Where a call was placed from. Stored on the call log as callContext and used by the staffer
 * metrics to split call time by context. A call without one (an older bundle) is stored as
 * 'unknown'.
 */
export type CallContextKind = 'job_order' | 'prefill' | 'data_cleanup' | 'directory'

export interface InitiateCallRequest {
  candidateId: string
  phoneNumber: string
  candidateName?: string
  jobOrderId?: string
  callContext?: CallContextKind
  /** The job order id, the prefill id or the data clean-up bucket (CleanupRange). */
  contextId?: string
}

/** Why the app ended a call itself. Sent as ?reason= on the DELETE. */
export type EndCallReason = 'not_connected_timeout' | 'cleanup'

export interface EndCallRequest {
  callLogId: string
  reason?: EndCallReason
}

export interface CallResponse {
  callLogId: string
  zoomCallId: string
  status: CallStatus
  candidateId: string
  candidateName?: string
  phoneNumber: string
  callStartedAt: string
}

export interface EndCallResponse {
  success: boolean
  message: string
  durationSeconds?: number
  zoomCallEnded?: boolean
}

export interface CallStatusResponse {
  callLogId: string
  zoomCallId: string
  status: CallStatus
  durationSeconds: number
  callStartedAt: string
  callAnsweredAt?: string
  candidateId?: string
  jobOrderId?: string
}

export interface ZoomConnectionStatusResponse {
  isConnected: boolean
  zoomAccountEmail?: string
  connectedAt?: string
  isZoomConfigured: boolean
  /**
   * The stored grant lacks a scope the server now needs (call history), so the user must
   * reconnect Zoom. Absent on an older server.
   */
  needsReconnect?: boolean
}

export interface CallHistoryItem {
  id: string
  recruiterName: string
  candidateName: string
  phoneNumber: string
  status: CallStatus
  callStartedAt: string
  callEndedAt?: string
  durationSeconds?: number
  durationFormatted?: string
  candidateAction?: string
  jobOrderId?: string
}

export interface CallHistoryPagingResponse {
  data: CallHistoryItem[]
  total: number
  page: number
  limit: number
}

export interface CallHistoryQuery {
  pageNumber?: number
  pageSize?: number
  status?: string
  startDate?: string
  endDate?: string
}

export interface HoldCallResponse {
  success: boolean
  message: string
  held: boolean
}

export interface TransferCallResponse {
  success: boolean
  message: string
}

export interface RecordCallResponse {
  success: boolean
  message: string
  recording: boolean
}

export interface MeetingFromCallResponse {
  joinUrl: string
  startUrl: string
  meetingId: number
}

export const zoomPhoneApi = createApi({
  reducerPath: 'ZoomPhoneApi',
  tagTypes: ['ZoomPhone', 'CallHistory'],
  baseQuery: baseQuery,
  endpoints: (builder) => ({
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    getZoomConnectionStatus: builder.query<ZoomConnectionStatusResponse, void>({
      query: () => ({
        url: '/api/zoom/oauth/connection-status',
        method: 'GET',
      }),
    }),

    initiateCall: builder.mutation<CallResponse, InitiateCallRequest>({
      query: (body) => ({
        url: '/api/zoom/phone/call',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CallHistory'],
    }),

    endCall: builder.mutation<EndCallResponse, EndCallRequest>({
      query: ({callLogId, reason}) => ({
        url: `/api/zoom/phone/call/${callLogId}`,
        method: 'DELETE',
        params: reason ? {reason} : undefined,
      }),
      invalidatesTags: ['CallHistory'],
    }),

    getActiveCall: builder.query<CallStatusResponse | null, void>({
      query: () => ({
        url: '/api/zoom/phone/call/active',
        method: 'GET',
      }),
    }),

    getCallStatus: builder.query<CallStatusResponse, string>({
      query: (callLogId) => ({
        url: `/api/zoom/phone/call/${callLogId}/status`,
        method: 'GET',
      }),
    }),

    getCallHistory: builder.query<
      CallHistoryPagingResponse,
      {candidateId: string; query?: CallHistoryQuery}
    >({
      query: ({candidateId, query}) => ({
        url: `/api/zoom/phone/candidates/${candidateId}/call-history`,
        method: 'GET',
        params: query,
      }),
      providesTags: ['CallHistory'],
    }),

    holdCall: builder.mutation<HoldCallResponse, {callLogId: string; hold: boolean}>({
      query: ({callLogId, hold}) => ({
        url: `/api/zoom/phone/call/${callLogId}/hold`,
        method: 'PATCH',
        body: {hold},
      }),
    }),

    transferCall: builder.mutation<
      TransferCallResponse,
      {callLogId: string; transferTo?: string; transferToExtension?: string}
    >({
      query: ({callLogId, ...body}) => ({
        url: `/api/zoom/phone/call/${callLogId}/transfer`,
        method: 'POST',
        body,
      }),
    }),

    recordCall: builder.mutation<RecordCallResponse, {callLogId: string; recording: boolean}>({
      query: ({callLogId, recording}) => ({
        url: `/api/zoom/phone/call/${callLogId}/record`,
        method: 'PATCH',
        body: {recording},
      }),
    }),

    createMeetingFromCall: builder.mutation<MeetingFromCallResponse, string>({
      query: (callLogId) => ({
        url: `/api/zoom/phone/call/${callLogId}/meet`,
        method: 'POST',
      }),
    }),
  }),
})

export const {
  useGetZoomConnectionStatusQuery,
  useLazyGetZoomConnectionStatusQuery,
  useInitiateCallMutation,
  useEndCallMutation,
  useLazyGetActiveCallQuery,
  useLazyGetCallStatusQuery,
  useGetCallHistoryQuery,
  useHoldCallMutation,
  useTransferCallMutation,
  useRecordCallMutation,
  useCreateMeetingFromCallMutation,
} = zoomPhoneApi
